import React, { useEffect, useMemo, useState } from "react";

import { Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, TabContent, TabPane } from "reactstrap";

import BreadCrumb from "../../Components/Common/BreadCrumb";
import { APIClient, setAuthorization } from "../../helpers/api_helper";
import ToastAlert from "../../Components/Common/ToastAlert";
import CustomTableContainer from "../../Components/Common/CustomTableContainer";

const Index = () => {
    const api = useMemo(() => new APIClient(), []);
    setAuthorization(localStorage.getItem("token"));

    const [companyList, setCompanyList] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [pageNumber, setPageNumber] = useState(1);
    const [loadingState, setLoadingState] = useState(false);
    const [maxPage, setMaxPage] = useState(1);
    const [nextPage, setNextPage] = useState(null);
    const [prevPage, setPrevPage] = useState(null);

    const [columns, setColumns] = useState([]);
    const [searchCompanyName, setSearchCompanyName] = useState("");
    const [intervalIdCompanyName, setIntervalIdCompanyName] = useState(null);

    const buildColumnsFromData = () => {
        return [
            { Header: "Company Name", accessor: "name" },
            {
                Header: "Total Jobs",
                accessor: "total_jobs",
                Cell: (cellProps) => {
                    return <span className="badge badge-soft-info">{cellProps.row.original.total_jobs || 0}</span>;
                }
            },
            {
                Header: "Open Jobs",
                accessor: "open_jobs",
                Cell: (cellProps) => {
                    return <span className="badge badge-soft-success">{cellProps.row.original.open_jobs || 0}</span>;
                }
            },
            {
                Header: "Avg. Closing Time",
                accessor: "avg_closing_days",
                Cell: (cellProps) => {
                    const days = cellProps.row.original.avg_closing_days;
                    return days ? `${Number(days).toFixed(1)} days` : '-';
                }
            },
            {
                Header: "Actions",
                Cell: (cellProps) => (
                    <a 
                        href={`/talent/jobs?fk_company_id=${cellProps.row.original.id}`}
                        className="btn btn-soft-primary btn-sm"
                    >
                        <i className="ri-list-check-2 align-bottom me-1"></i>
                        Job Posts
                    </a>
                ),
                sortable: false,
            },
        ];
    };

    const getCompanyList = async () => {
        setLoadingState(true);
        try {
            let url = `v1/companies/with-stats?page=${pageNumber}`;
            if (searchCompanyName && searchCompanyName.length > 0) {
                url += `&company_name=${encodeURIComponent(searchCompanyName)}`;
            }
            const response = await api.get(url);
            // Handle new PaginationResponse structure
            if (response && response.data && Array.isArray(response.data)) {
                setCompanyList(response.data);
                setRowCount(response.count || 0);
                setMaxPage(response.max_page || 1);
                setNextPage(response.next);
                setPrevPage(response.prev);
                setColumns(buildColumnsFromData());
            } else if (Array.isArray(response)) {
                // Fallback for old response format
                setCompanyList(response);
                setRowCount(response.length || 0);
                setMaxPage(Math.ceil((response.length || 0) / pageSize));
                setColumns(buildColumnsFromData());
            } else if (response && Array.isArray(response.results)) {
                // Fallback for old response format
                setCompanyList(response.results);
                setRowCount(response.count || response.results.length || 0);
                setMaxPage(Math.ceil((response.count || 0) / pageSize));
                setColumns(buildColumnsFromData());
            } else {
                setCompanyList([]);
                setRowCount(0);
                setMaxPage(1);
            }
        } catch (error) {
            ToastAlert(error, "error");
        } finally {
            setLoadingState(false);
        }
    };



    useEffect(() => {
        getCompanyList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, pageSize]);

    useEffect(() => {
        setPageNumber(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchCompanyName]);

    useEffect(() => {
        if (pageNumber > 1) {
            setPageNumber(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize]);

    document.title = "Hiring companies · Horus";

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Hiring companies" pageTitle="Talent" />
                    <Row>
                        <Col xs={12}>
                            <div>
                                <Card>
                                    
                                    <CardBody className="border-bottom-dashed border-bottom">
                                        <div className="d-flex align-items-center gap-2 flex-wrap w-100">
                                            <div className="search-box" style={{ minWidth: 300, flex: 1 }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Filter by company name"
                                                    onChange={(e) => {
                                                        setLoadingState(true);
                                                        clearTimeout(intervalIdCompanyName);
                                                        const newTimeoutId = setTimeout(() => {
                                                            setSearchCompanyName(e.target.value);
                                                        }, 300);
                                                        setIntervalIdCompanyName(newTimeoutId);
                                                    }}
                                                />
                                                <i className="ri-search-line search-icon"></i>
                                            </div>
                                        </div>
                                    </CardBody>
                                    <CardBody>
                                        <TabContent className="text-muted">
                                            <TabPane>
                                                <div id="table-company-list-all" className="table-card gridjs-border-none pb-2">
                                                    {!loadingState ? (
                                                        companyList && companyList.length > 0 ? (
                                                            <CustomTableContainer
                                                                columns={columns}
                                                                data={companyList}
                                                                rowCount={rowCount}
                                                                maxPage={maxPage}
                                                                handlePageChange={setPageNumber}
                                                                handlePageSize={setPageSize}
                                                                currentPageSize={pageSize}
                                                                currentPage={pageNumber}
                                                                divClass="table-responsive mb-1"
                                                                tableClass="mb-0 align-middle table-borderless"
                                                                theadClass="table-light text-muted"
                                                                showCheckbox={false}
                                                            />
                                                        ) : (
                                                            <div className="py-4 text-center">
                                                                <div>
                                                                    <lord-icon
                                                                        src="https://cdn.lordicon.com/msoeawqm.json"
                                                                        trigger="loop"
                                                                        colors="primary:#405189,secondary:#0ab39c"
                                                                        style={{ width: "72px", height: "72px" }}
                                                                    ></lord-icon>
                                                                </div>
                                                                <div className="mt-4">
                                                                    <h5>No companies found. Use the search or add a new company.</h5>
                                                                </div>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <div className="py-4 text-center">
                                                            <Spinner color="primary" />
                                                        </div>
                                                    )}
                                                </div>
                                            </TabPane>
                                        </TabContent>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Ekle/Düzenle modalları kaldırıldı */}


        </React.Fragment>
    );
};

export default Index;



